import { useRef, useMemo, LegacyRef, MutableRefObject } from 'react';
import {
  useForm as useHookForm,
  Control,
  Path,
  type RegisterOptions,
  UseFormProps as HookUseFormProps,
  UseFormRegisterReturn,
  UseFormHandleSubmit,
  UseFormGetValues,
  UseFormSetValue,
  UseFormWatch,
  UseFormStateReturn,
  FieldErrors,
} from 'react-hook-form';

// @ts-expect-error: types
export interface Field extends RegisterOptions {
  caption: string;
  description?: string;
}

export type Fields<UseFormData> = Record<keyof UseFormData, Field>;

export interface UseFormField {
  name: string;
  caption?: string;
  description?: string;
  register: () => UseFormRegisterReturn;
  reset: () => void;
  error?: string;
}

type UseFormFields<UseFormData> = Record<keyof UseFormData, UseFormField>;

export interface UseFormProps<UseFormData extends object, Context extends object> extends HookUseFormProps<UseFormData, Context> {
  title?: string;
  description?: string;
  fields: Fields<UseFormData>;
}

export interface UseFormReturn<UseFormData extends object, Context extends object> {
  title?: string;
  description?: string;
  fields: UseFormFields<UseFormData>;
  control: Control<UseFormData, Context>;
  handleSubmit: UseFormHandleSubmit<UseFormData>;
  getValues: UseFormGetValues<UseFormData>;
  setValue: UseFormSetValue<UseFormData>;
  watch: UseFormWatch<UseFormData>;
  refForm?: LegacyRef<HTMLFormElement>;
  formState: UseFormStateReturn<UseFormData>;
  errors: FieldErrors<UseFormData>;
  formElement?: HTMLFormElement;
}

export const useForm = <UseFormData extends object, Context extends object = object>(options: UseFormProps<UseFormData, Context>) => {
  const refForm = useRef<HTMLFormElement>() as MutableRefObject<HTMLFormElement>;
  const { title, description, fields: optionsFields, ...hookOptions } = options;
  const { reset, register, resetField, trigger, handleSubmit, control, formState, getValues, setValue, watch } = useHookForm(hookOptions);
  const { errors } = formState;
  const fields = useMemo(() => {
    const fields = {} as UseFormFields<UseFormData>;
    for (const key in optionsFields) {
      const name = key as keyof UseFormData;
      const field = optionsFields[name];
      const errorKey = key as any;
      const error = errors && (errors as any)[errorKey];
      fields[name] = {
        name,
        caption: field.caption,
        description: field.description,
        reset: () => resetField(name as string as Path<UseFormData>),
        // @ts-expect-error types
        register: () => register(name as string as Path<UseFormData>, field),
        error: error?.message,
      } as UseFormField;
    }
    return fields;
  }, [optionsFields, errors, register, resetField]);
  const result = {
    title,
    description,
    fields,
    reset,
    trigger,
    handleSubmit,
    control,
    getValues,
    setValue,
    watch,
    formState,
    errors,
    refForm,
    formElement: refForm.current,
  };
  return result;
};
